import { motion } from 'framer-motion';

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

export default function Terms() {
  return (
    <div className="pt-28 bg-black min-h-screen" data-testid="terms-page">
      {/* Header */}
      <section className="py-20 md:py-28 border-b border-[#1a1a1a]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer}>
            <motion.p variants={fadeInUp} className="text-sm uppercase tracking-[0.3em] text-[#D4AF37] mb-4">
              Legal
            </motion.p>
            <motion.h1 variants={fadeInUp} className="font-serif text-4xl md:text-5xl text-white mb-6">
              Terms of Service
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-[#999999]">
              Last updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div 
            initial="hidden" 
            animate="visible" 
            variants={staggerContainer}
            className="prose prose-invert max-w-none"
          >
            <motion.div variants={fadeInUp} className="mb-12">
              <h2 className="font-serif text-2xl text-white mb-4">1. Agreement to Terms</h2>
              <p className="text-[#999999] leading-relaxed">
                By accessing or using The Signal website and services, you agree to be bound by these Terms of Service. 
                If you do not agree to these terms, please do not use our services.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="mb-12">
              <h2 className="font-serif text-2xl text-white mb-4">2. Company Information</h2>
              <p className="text-[#999999] leading-relaxed mb-4">
                The Signal is operated by:
              </p>
              <div className="bg-[#0f0f0f] border border-[#1a1a1a] p-6 text-[#999999]">
                <p><strong className="text-white">Business Type:</strong> Eenmanszaak</p>
                <p><strong className="text-white">KVK Number:</strong> 42033086</p>
                <p><strong className="text-white">Vestigingsnummer:</strong> 000065359429</p>
                <p><strong className="text-white">Address:</strong> Gasthuisstraat 27, 5931NT Tegelen, Netherlands</p>
                <p><strong className="text-white">Email:</strong> contact@thesignalcore.com</p>
              </div>
            </motion.div>

            <motion.div variants={fadeInUp} className="mb-12">
              <h2 className="font-serif text-2xl text-white mb-4">3. Products and Services</h2>
              <p className="text-[#999999] leading-relaxed mb-4">
                The Signal offers digital educational content and coaching services focused on male self-improvement, 
                including but not limited to guidance on skincare, grooming, fitness, style, and confidence.
              </p>
              <p className="text-[#999999] leading-relaxed">
                <strong className="text-white">The Signal: Upgrade</strong> - A one-time purchase digital guide with lifetime access.<br/>
                <strong className="text-white">1-on-1 Coaching</strong> - A monthly subscription service for personalized guidance.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="mb-12">
              <h2 className="font-serif text-2xl text-white mb-4">4. Digital Product Delivery</h2>
              <p className="text-[#999999] leading-relaxed">
                Upon successful payment, digital products are made available through your account on our website. 
                Content is provided for viewing only and may not be downloaded, copied, or redistributed. 
                Access requires an account using the email address used at checkout.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="mb-12">
              <h2 className="font-serif text-2xl text-white mb-4">5. Payments and Refunds</h2>
              <p className="text-[#999999] leading-relaxed mb-4">
                All payments are processed securely through Shopify. Prices are displayed in Euros (€).
              </p>
              
              <div className="bg-[#0f0f0f] border border-[#D4AF37]/30 p-6 mb-4">
                <h3 className="text-[#D4AF37] font-semibold mb-3">Right of Withdrawal for Digital Content (EU Consumer Rights)</h3>
                <p className="text-[#999999] leading-relaxed mb-3">
                  In accordance with Article 16(m) of the EU Consumer Rights Directive (2011/83/EU), the following applies to digital content:
                </p>
                <p className="text-[#999999] leading-relaxed mb-3">
                  <strong className="text-white">Waiver of withdrawal right:</strong> By completing a purchase on The Signal, you expressly consent to immediate access to the digital content after payment. You acknowledge that you waive your statutory 14-day right of withdrawal once delivery of the digital content has begun.
                </p>
                <p className="text-[#999999] leading-relaxed">
                  <strong className="text-white">This means:</strong> Because the digital content is made available immediately after purchase and you have consented to this, returns or refunds are not possible.
                </p>
              </div>

              <p className="text-[#999999] leading-relaxed mb-4">
                <strong className="text-white">Technical issues:</strong> If you cannot access your purchase due to technical problems on our end, please contact us within 14 days. We will resolve this as quickly as possible or provide an appropriate solution.
              </p>
              <p className="text-[#999999] leading-relaxed">
                <strong className="text-white">Subscription Cancellation:</strong> Monthly coaching subscriptions can be cancelled at any time. Access continues until the end of the current billing period.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="mb-12">
              <h2 className="font-serif text-2xl text-white mb-4">6. Intellectual Property</h2>
              <p className="text-[#999999] leading-relaxed">
                All content, including text, graphics, images, and digital materials provided through The Signal, 
                is owned by us and protected by copyright laws. You may not reproduce, distribute, or create 
                derivative works without explicit written permission.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="mb-12 bg-[#1a0a0a] border border-red-900/30 p-6">
              <h2 className="font-serif text-2xl text-red-400 mb-4">7. Prohibited Sharing & Legal Action</h2>
              <p className="text-[#999999] leading-relaxed mb-4">
                <strong className="text-white">Sharing purchased content is strictly prohibited.</strong> The content you purchase 
                is licensed for your personal use only. You may not share, distribute, resell, or make available 
                any purchased materials to third parties, whether for free or for payment.
              </p>
              <p className="text-[#999999] leading-relaxed mb-4">
                <strong className="text-white">Legal consequences:</strong> Unauthorized sharing of our content constitutes 
                copyright infringement and breach of contract. We actively monitor for unauthorized distribution. 
                If we discover that you have shared purchased content with others who have not paid for access, 
                we reserve the right to:
              </p>
              <ul className="text-[#999999] list-disc list-inside space-y-2 mb-4">
                <li>Immediately terminate your access without refund</li>
                <li>Pursue legal action for copyright infringement</li>
                <li>Seek damages including lost revenue and legal costs</li>
                <li>Report the infringement to relevant authorities</li>
              </ul>
              <p className="text-[#999999] leading-relaxed">
                <strong className="text-red-400">Each purchase is for a single user only.</strong> If you want others to access 
                our content, they must purchase their own license. Violation of this policy will be taken seriously 
                and may result in legal proceedings.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="mb-12">
              <h2 className="font-serif text-2xl text-white mb-4">8. Disclaimer</h2>
              <p className="text-[#999999] leading-relaxed">
                The Signal provides educational content only. Results vary based on individual effort and circumstances. 
                Our content is not medical, financial, or professional advice. Consult appropriate professionals 
                for specific concerns. We make no guarantees regarding specific outcomes or results.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="mb-12">
              <h2 className="font-serif text-2xl text-white mb-4">9. User Conduct</h2>
              <p className="text-[#999999] leading-relaxed">
                You agree not to: share your account credentials, redistribute our content, use our services 
                for illegal purposes, or attempt to circumvent our content protection measures.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="mb-12">
              <h2 className="font-serif text-2xl text-white mb-4">10. Limitation of Liability</h2>
              <p className="text-[#999999] leading-relaxed">
                To the maximum extent permitted by law, The Signal shall not be liable for any indirect, 
                incidental, or consequential damages arising from your use of our services.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="mb-12">
              <h2 className="font-serif text-2xl text-white mb-4">11. Governing Law</h2>
              <p className="text-[#999999] leading-relaxed">
                These terms are governed by the laws of the Netherlands. Any disputes shall be subject 
                to the exclusive jurisdiction of the courts in the Netherlands.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="mb-12">
              <h2 className="font-serif text-2xl text-white mb-4">12. Contact</h2>
              <p className="text-[#999999] leading-relaxed">
                For questions about these Terms of Service, please contact us at:{' '}
                <a href="mailto:contact@thesignalcore.com" className="text-[#D4AF37] hover:underline">
                  contact@thesignalcore.com
                </a>
              </p>
            </motion.div>

            {/* Dutch Section */}
            <motion.div variants={fadeInUp} className="mt-16 pt-12 border-t border-[#1a1a1a]">
              <p className="text-sm uppercase tracking-[0.3em] text-[#D4AF37] mb-6">
                Nederlandse Vertaling
              </p>
              
              <div className="bg-[#0f0f0f] border border-[#1a1a1a] p-6 md:p-8">
                <h3 className="font-serif text-xl text-white mb-4">Herroepingsrecht voor Digitale Inhoud</h3>
                <p className="text-[#999999] leading-relaxed mb-4">
                  Op grond van artikel 6:230p sub e van het Burgerlijk Wetboek en de Europese Richtlijn 
                  Consumentenrechten (2011/83/EU) geldt het volgende voor digitale inhoud:
                </p>
                <p className="text-[#999999] leading-relaxed mb-4">
                  <strong className="text-white">Afstand van herroepingsrecht:</strong> Door een aankoop te voltooien 
                  op The Signal, geeft u uitdrukkelijk toestemming om direct na betaling toegang te krijgen tot de 
                  digitale inhoud. U erkent hiermee dat u afstand doet van uw wettelijke herroepingsrecht van 14 dagen 
                  zodra de levering van de digitale inhoud is begonnen.
                </p>
                <p className="text-[#999999] leading-relaxed mb-4">
                  <strong className="text-white">Dit betekent:</strong> Omdat de digitale inhoud direct na aankoop 
                  beschikbaar wordt gesteld en u hiermee heeft ingestemd, is retourneren of terugbetaling niet mogelijk.
                </p>
                <p className="text-[#999999] leading-relaxed">
                  <strong className="text-white">Technische problemen:</strong> Indien u door technische problemen aan 
                  onze kant geen toegang krijgt tot uw aankoop, neem dan binnen 14 dagen contact met ons op via{' '}
                  <a href="mailto:contact@thesignalcore.com" className="text-[#D4AF37] hover:underline">
                    contact@thesignalcore.com
                  </a>
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
